import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { I } from '../../../../common/v5/config';

const SidebarContainer = styled.div`
  width: ${props => props.collapsed ? '60px' : '320px'};
  height: 100%;
  background-color: var(--cention-color-background-light);
  border-right: var(--cention-border-width-base) solid var(--cention-color-border-base);
  display: flex;
  flex-direction: column;
  transition: width 150ms ease-in-out;
  z-index: 50;
  position: relative;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--cention-spacing-s) var(--cention-spacing-m);
  border-bottom: var(--cention-border-width-base) solid var(--cention-color-border-base);
  background-color: var(--cention-color-background-xlight);
  height: 60px;
  box-sizing: border-box;
`;

const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: var(--cention-color-text-dark);
  margin: 0;
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  padding: var(--cention-spacing-xs);
  border-radius: var(--cention-border-radius-small);
  cursor: pointer;
  color: var(--cention-color-text-base);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  
  &:hover {
    background-color: var(--cention-color-background-base);
  }
  
  i {
    font-size: 14px;
    transition: transform 150ms ease-in-out;
    transform: ${props => props.collapsed ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const SearchContainer = styled.div`
  padding: var(--cention-spacing-m);
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
  pointer-events: ${props => props.collapsed ? 'none' : 'auto'};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: var(--cention-spacing-s) var(--cention-spacing-s) var(--cention-spacing-s) 36px;
  border: var(--cention-border-width-base) solid var(--cention-color-border-base);
  border-radius: var(--cention-border-radius-base);
  font-size: 13px;
  background-color: white;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%23666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>');
  background-repeat: no-repeat;
  background-position: 12px center;
  background-size: 16px;
  
  &:focus {
    outline: none;
    border-color: var(--cention-color-primary);
    box-shadow: 0 0 0 2px var(--cention-color-primary-tint-2);
  }
  
  &::placeholder {
    color: var(--cention-color-text-light);
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: var(--cention-spacing-s);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--cention-color-border-base);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--cention-color-text-lighter);
  }
`;

const NodeSection = styled.div`
  margin-bottom: var(--cention-spacing-m);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--cention-spacing-s) var(--cention-spacing-m);
  font-size: 12px;
  font-weight: 600;
  color: var(--cention-color-text-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: var(--cention-color-background-base);
  border-bottom: var(--cention-border-width-base) solid var(--cention-color-border-base);
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
`;

const AddButton = styled.button`
  background: none;
  border: none;
  color: var(--cention-color-text-base);
  cursor: pointer;
  padding: 2px 4px;
  border-radius: var(--cention-border-radius-small);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  transition: all 150ms ease-in-out;
  opacity: ${props => props.collapsed ? 0 : 0.7};
  pointer-events: ${props => props.collapsed ? 'none' : 'auto'};

  &:hover {
    background-color: var(--cention-color-background-light);
    color: var(--cention-color-primary);
    opacity: 1;
  }

  &:active {
    background-color: var(--cention-color-background-dark);
  }

  i {
    font-size: 12px;
  }
`;

const NodeList = styled.div`
  padding: var(--cention-spacing-xs) 0;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--cention-color-border-base);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--cention-color-text-lighter);
  }
  
  /* Fade effect at bottom when scrollable */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(transparent, var(--cention-color-background-light));
    pointer-events: none;
    opacity: ${props => props.hasScroll ? 1 : 0};
    transition: opacity 150ms ease-in-out;
  }
`;

const NodeItem = styled.div`
  display: flex;
  align-items: center;
  padding: var(--cention-spacing-s) ${props => props.collapsed ? 'var(--cention-spacing-s)' : 'var(--cention-spacing-m)'};
  cursor: pointer;
  transition: all 150ms ease-in-out;
  border-radius: 0;
  position: relative;
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  
  &:hover {
    background-color: var(--cention-color-background-base);
  }
  
  &:active {
    background-color: var(--cention-color-background-dark);
  }
`;

const NodeIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: var(--cention-border-radius-base);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  margin-right: ${props => props.collapsed ? '0' : 'var(--cention-spacing-s)'};
  background: ${props => {
    switch(props.type) {
      case 'agent': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'tool': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'model': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
      case 'memory': return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
      default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  }};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const NodeInfo = styled.div`
  flex: 1;
  min-width: 0;
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
  pointer-events: ${props => props.collapsed ? 'none' : 'auto'};
`;

const NodeTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--cention-color-text-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

const NodeDescription = styled.div`
  font-size: 11px;
  color: var(--cention-color-text-light);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
  line-height: 1.2;
`;

const EmptyState = styled.div`
  padding: var(--cention-spacing-l);
  text-align: center;
  color: var(--cention-color-text-light);
  font-size: 12px;
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
  
  i {
    font-size: 24px;
    margin-bottom: var(--cention-spacing-s);
    display: block;
    color: var(--cention-color-text-lighter);
  }
`;

const Tooltip = styled.div`
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: var(--cention-spacing-s);
  background: var(--cention-color-text-dark);
  color: white;
  padding: var(--cention-spacing-2xs) var(--cention-spacing-xs);
  border-radius: var(--cention-border-radius-small);
  font-size: 11px;
  white-space: nowrap;
  z-index: 1000;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease-in-out;
  
  &::before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 4px solid transparent;
    border-right-color: var(--cention-color-text-dark);
  }
  
  ${NodeItem}:hover & {
    opacity: ${props => props.collapsed ? 1 : 0};
  }
`;

const ContextMenu = styled.div`
  background: #2a2a2a;
  border: 1px solid #404040;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  min-width: 120px;
  overflow: hidden;
  position: fixed;
  z-index: 9999;
  pointer-events: auto;
`;

const ContextMenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-size: 12px;
  color: #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #404040;
  }
  
  &.destructive {
    color: #ff6b6b;
    
    &:hover {
      background: rgba(255, 107, 107, 0.1);
    }
  }
  
  i {
    margin-right: 8px;
    width: 12px;
    text-align: center;
  }
`;

const getNodeIcon = (type) => {
  switch(type) {
    case 'agent': return '🤖';
    case 'tool': return '🔧';
    default: return '📦';
  }
};

const CentionSidebar = ({ 
  collapsed = false,
  onToggleCollapse,
  agents = [],
  tools = [],
  onNodeClick,
  onCreateAgent,
  onCreateTool,
  onEditAgent,
  onDeleteAgent,
  onEditTool,
  onDeleteTool,
  isDeletingAgent = false,
  isDeletingTool = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contextMenu, setContextMenu] = useState(null);

  const filterItems = (items) => {
    if (!searchTerm) return items;
    const searchLower = searchTerm.toLowerCase();
    return items.filter(item => 
      (item.name || item.title || item.agent_name || '').toLowerCase().includes(searchLower) ||
      (item.description || '').toLowerCase().includes(searchLower)
    );
  };

  const handleNodeClick = (node, type) => {
    console.log('Sidebar: Node clicked:', node.name || node.title, 'Type:', type);
    onNodeClick?.(node, type);
  };

  const handleContextMenu = (e, item, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    
    // Calculate smart positioning to avoid cropping
    const menuHeight = 80; // Approximate height of context menu (2 items)
    const menuWidth = 120; // Approximate width of context menu
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    let x = e.clientX;
    let y = e.clientY;
    
    // Adjust horizontal position if menu would go off-screen
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 10; // 10px margin from edge
    }
    
    // Adjust vertical position if menu would go off-screen
    if (y + menuHeight > viewportHeight) {
      y = y - menuHeight; // Show above the cursor instead
    }
    
    // Ensure minimum margins from edges
    x = Math.max(10, x);
    y = Math.max(10, y);
    
    setContextMenu({
      x,
      y,
      item,
      type
    });
  };

  const handleContextMenuAction = (action, item, type) => {
    setContextMenu(null);
    
    switch (action) {
      case 'edit':
        if (type === 'agent' && onEditAgent && !isDeletingAgent) {
          onEditAgent(item);
        } else if (type === 'tool' && onEditTool && !isDeletingTool) {
          onEditTool(item);
        }
        break;
      case 'delete':
        if (type === 'agent' && onDeleteAgent && !isDeletingAgent) {
          if (window.confirm(`Are you sure you want to delete ${item.name || item.title}?`)) {
            onDeleteAgent(item.agent_id || item.id);
          }
        } else if (type === 'tool' && onDeleteTool && !isDeletingTool) {
          if (window.confirm(`Are you sure you want to delete ${item.name || item.title}?`)) {
            onDeleteTool(item.tool_id || item.id);
          }
        }
        break;
    }
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
    };

    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  const renderNodeSection = (title, items, type) => {
    const filteredItems = filterItems(items);
    const hasScroll = filteredItems.length > 8; // Show scroll indicator if more than 8 items
    
    return (
      <NodeSection key={type}>
        <SectionHeader collapsed={collapsed}>
          <span>{title}</span>
          {type === 'agent' && onCreateAgent && (
            <AddButton
              collapsed={collapsed}
              onClick={(e) => {
                e.stopPropagation();
                onCreateAgent();
              }}
              title={I('Create new agent')}
            >
              <i className="fa fa-plus" />
            </AddButton>
          )}
          {type === 'tool' && onCreateTool && (
            <AddButton
              collapsed={collapsed}
              onClick={(e) => {
                e.stopPropagation();
                onCreateTool();
              }}
              title={I('Create new tool')}
            >
              <i className="fa fa-plus" />
            </AddButton>
          )}
        </SectionHeader>
        <NodeList hasScroll={hasScroll}>
          {filteredItems.length === 0 ? (
            <EmptyState collapsed={collapsed}>
              <i className="fa fa-inbox" />
              {I(`No ${type}s available`)}
            </EmptyState>
          ) : (
            filteredItems.map((item, index) => (
              <NodeItem
                key={item.id || item.agent_id || item.tool_id || index}
                collapsed={collapsed}
                onClick={() => handleNodeClick(item, type)}
                onContextMenu={(e) => handleContextMenu(e, item, type)}
                title={collapsed ? (item.name || item.title || item.agent_name || item.agent_id) : ''}
              >
                <NodeIcon type={type} collapsed={collapsed}>
                  {getNodeIcon(type)}
                </NodeIcon>
                <NodeInfo collapsed={collapsed}>
                  <NodeTitle>{item.name || item.title || item.agent_name || item.agent_id}</NodeTitle>
                  {item.description && (
                    <NodeDescription>{item.description}</NodeDescription>
                  )}
                </NodeInfo>
                {collapsed && (
                  <Tooltip collapsed={collapsed}>
                    {item.name || item.title || item.agent_name || item.agent_id}
                    {item.description && (
                      <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.8 }}>
                        {item.description}
                      </div>
                    )}
                  </Tooltip>
                )}
              </NodeItem>
            ))
          )}
        </NodeList>
      </NodeSection>
    );
  };

  const sections = [
    { title: I('Agents'), items: agents, type: 'agent' },
    { title: I('Tools'), items: tools, type: 'tool' }
  ];

  return (
    <>
      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          style={{
            top: contextMenu.y,
            left: contextMenu.x
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <ContextMenuItem
            onClick={() => handleContextMenuAction('edit', contextMenu.item, contextMenu.type)}
            style={{ 
              opacity: (contextMenu.type === 'agent' && isDeletingAgent) || (contextMenu.type === 'tool' && isDeletingTool) ? 0.5 : 1,
              cursor: (contextMenu.type === 'agent' && isDeletingAgent) || (contextMenu.type === 'tool' && isDeletingTool) ? 'not-allowed' : 'pointer'
            }}
          >
            <i className="fa fa-edit" />
            {I('Edit')}
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() => handleContextMenuAction('delete', contextMenu.item, contextMenu.type)}
            className="destructive"
            style={{ 
              opacity: (contextMenu.type === 'agent' && isDeletingAgent) || (contextMenu.type === 'tool' && isDeletingTool) ? 0.5 : 1,
              cursor: (contextMenu.type === 'agent' && isDeletingAgent) || (contextMenu.type === 'tool' && isDeletingTool) ? 'not-allowed' : 'pointer'
            }}
          >
            <i className="fa fa-trash" />
            {(contextMenu.type === 'agent' && isDeletingAgent) || (contextMenu.type === 'tool' && isDeletingTool) 
              ? I('Deleting...') 
              : I('Delete')}
          </ContextMenuItem>
        </ContextMenu>
      )}
    <SidebarContainer collapsed={collapsed}>
      <SidebarHeader>
        <SidebarTitle collapsed={collapsed}>
          {I('Node Creator')}
        </SidebarTitle>
        <CollapseButton 
          collapsed={collapsed}
          onClick={onToggleCollapse}
          title={collapsed ? I('Expand sidebar') : I('Collapse sidebar')}
        >
          <i className="fa fa-chevron-left" />
        </CollapseButton>
      </SidebarHeader>

      {!collapsed && (
        <SearchContainer collapsed={collapsed}>
          <SearchInput
            type="text"
            placeholder={I('Search nodes...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
      )}

      <SidebarContent>
        {sections.map(section => 
          renderNodeSection(section.title, section.items, section.type)
        )}
      </SidebarContent>
    </SidebarContainer>
    </>
  );
};

export default CentionSidebar; 